import {
  keepPreviousData,
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryKey,
} from '@tanstack/react-query';
import { keys } from './keys';
import { invalidate, type QueryOptions } from './query-client';
import { request } from './client';
import type {
  BillingInterval,
  Plan,
  PlanStatus,
  Solution,
  SolutionSlug,
  Subscription,
  TenantFeatures,
  TenantOnboarding,
} from './types';

/**
 * /solutions, /plans, /tenants/:id/subscriptions and /tenants/:id/onboarding:
 * the catalog, who is on what, and how far each tenant's onboarding has come.
 * Prices are not editable from the hub; they live on the marketing site.
 */
export const plansApi = {
  solutions: () => request<Solution[]>('/solutions'),
  list: (query: { solution?: SolutionSlug } = {}) => request<Plan[]>('/plans', { query }),
  create: (body: Partial<Plan> & { solution: SolutionSlug; slug: string; name: string }) =>
    request<Plan>('/plans', { method: 'POST', body }),
  update: (
    id: string,
    body: Partial<
      Pick<
        Plan,
        | 'name'
        | 'tagline'
        | 'description'
        | 'bestFor'
        | 'includedUsers'
        | 'limits'
        | 'features'
        | 'isPublic'
        | 'isRecommended'
        | 'sortOrder'
      >
    > & { status?: PlanStatus; version?: number },
  ) => request<Plan>(`/plans/${id}`, { method: 'PATCH', body }),
  subscriptions: (tenantId: string) =>
    request<Subscription[]>(`/tenants/${tenantId}/subscriptions`),
  features: (tenantId: string) => request<TenantFeatures>(`/tenants/${tenantId}/features`),
  /** Staff: put the tenant on a plan (the previous live one for that solution is canceled). */
  assign: (
    tenantId: string,
    body: {
      solution: SolutionSlug;
      planId: string;
      billingInterval?: BillingInterval;
      trialDays?: number;
      notes?: string;
    },
  ) => request<Subscription>(`/tenants/${tenantId}/subscriptions`, { method: 'POST', body }),
  cancel: (tenantId: string, subscriptionId: string) =>
    request<Subscription>(`/tenants/${tenantId}/subscriptions/${subscriptionId}/cancel`, {
      method: 'POST',
    }),
  /** The checklist of what the tenant's platform plan includes (staff any tenant; a tenant its own). */
  onboarding: (tenantId: string) => request<TenantOnboarding>(`/tenants/${tenantId}/onboarding`),
  /** Staff (tenants:write): mark one item activated or not, with a note. Answers the whole checklist. */
  setOnboardingItem: (
    tenantId: string,
    featureKey: string,
    body: { activated: boolean; notes?: string | null },
  ) =>
    request<TenantOnboarding>(`/tenants/${tenantId}/onboarding/${featureKey}`, {
      method: 'PATCH',
      body,
    }),
};

type PlansQuery = NonNullable<Parameters<typeof plansApi.list>[0]>;
type CreatePlanBody = Parameters<typeof plansApi.create>[0];
type UpdatePlanBody = Parameters<typeof plansApi.update>[1];
type AssignPlanBody = Parameters<typeof plansApi.assign>[1];
type OnboardingItemBody = Parameters<typeof plansApi.setOnboardingItem>[2];

/** The catalog: every solution with its tiers. Shared by the portal and staff subscription pages. */
export function useSolutions(options: QueryOptions = {}) {
  return useQuery({
    queryKey: keys.plans.solutions,
    queryFn: () => plansApi.solutions(),
    ...options,
  });
}

export function usePlans(query: PlansQuery = {}, options: QueryOptions = {}) {
  return useQuery({
    queryKey: keys.plans.list(query),
    queryFn: () => plansApi.list(query),
    placeholderData: keepPreviousData,
    ...options,
  });
}

/** What one tenant is on. Idle until the tenant id is known. */
export function useSubscriptions(tenantId: string | null, options: QueryOptions = {}) {
  return useQuery({
    queryKey: keys.plans.subscriptions(tenantId),
    queryFn: tenantId ? () => plansApi.subscriptions(tenantId) : skipToken,
    ...options,
  });
}

export function useTenantFeatures(tenantId: string | null, options: QueryOptions = {}) {
  return useQuery({
    queryKey: keys.plans.features(tenantId),
    queryFn: tenantId ? () => plansApi.features(tenantId) : skipToken,
    ...options,
  });
}

/** The onboarding checklist for one tenant. Idle until the tenant id is known. */
export function useTenantOnboarding(tenantId: string | null, options: QueryOptions = {}) {
  return useQuery({
    queryKey: keys.onboarding.tenant(tenantId),
    queryFn: tenantId ? () => plansApi.onboarding(tenantId) : skipToken,
    ...options,
  });
}

/**
 * A plan or subscription change also changes what the WhatsApp config reports
 * (feature flag, plan); a subscription change also resets the onboarding checklist.
 */
function usePlanMutation<TVars, TData>(
  mutationFn: (vars: TVars) => Promise<TData>,
  ...alsoInvalidate: QueryKey[]
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => invalidate(queryClient, keys.plans.all, keys.whatsapp.all, ...alsoInvalidate),
  });
}

export function useCreatePlan() {
  return usePlanMutation((body: CreatePlanBody) => plansApi.create(body));
}

export function useUpdatePlan() {
  return usePlanMutation(({ id, body }: { id: string; body: UpdatePlanBody }) =>
    plansApi.update(id, body),
  );
}

export function useAssignPlan() {
  return usePlanMutation(
    ({ tenantId, body }: { tenantId: string; body: AssignPlanBody }) =>
      plansApi.assign(tenantId, body),
    keys.onboarding.all,
  );
}

export function useCancelSubscription() {
  return usePlanMutation(
    ({ tenantId, subscriptionId }: { tenantId: string; subscriptionId: string }) =>
      plansApi.cancel(tenantId, subscriptionId),
    keys.onboarding.all,
  );
}

/**
 * Activate or reset one checklist item. The API answers with the whole checklist,
 * which lands in the cache at once; the invalidation catches any other copy.
 */
export function useSetOnboardingItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      tenantId,
      featureKey,
      body,
    }: {
      tenantId: string;
      featureKey: string;
      body: OnboardingItemBody;
    }) => plansApi.setOnboardingItem(tenantId, featureKey, body),
    onSuccess: (data, { tenantId }) => {
      queryClient.setQueryData(keys.onboarding.tenant(tenantId), data);
      return invalidate(queryClient, keys.onboarding.all);
    },
  });
}
